import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from '../utils/toast';
import { getApiErrorMessage } from '@/utils/apiError';
import { Plus, Search, Edit, XCircle, Bell, Users, UserCog, ClipboardList, ChevronDown, MoreVertical } from 'lucide-react';
import axios from 'axios';
import UserManagementTab from '@/components/UserManagementTab';
import LeadFormModal from '@/components/LeadFormModal';
import { DISPOSITION_STATUSES, DEFAULT_DISPOSITION_SELECTION } from '@/utils/dispositionStatuses';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;
const DISPOSITION_STATUS_OPTIONS = DISPOSITION_STATUSES;

const CRMDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState('leads');
  const [currentPage, setCurrentPage] = useState(1);
  const [leadOwnerOptions, setLeadOwnerOptions] = useState([]);
  const [isViewMode, setIsViewMode] = useState(false);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeadIdForEdit, setSelectedLeadIdForEdit] = useState(null);

  // Bulk reassign (Super Admin only)
  const [selectedLeadIds, setSelectedLeadIds] = useState(() => new Set());
  const [reassignOpen, setReassignOpen] = useState(false);
  const [confirmReassignOpen, setConfirmReassignOpen] = useState(false);
  const [reassignTargetId, setReassignTargetId] = useState('');
  const [reassigning, setReassigning] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    dispositionStatuses: [...DEFAULT_DISPOSITION_SELECTION],
    leadOwner: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      toast.error('Please sign in to access CRM');
      navigate('/signin');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    
    // Check if user has CRM access
    const crmRoles = ['Super Admin', 'Exlva Admin', 'ExlvaAdmin', 'Admin', 'CRM Officer', 'CRM Associate'];
    if (!crmRoles.includes(parsedUser.role)) {
      toast.error('You don\'t have access to CRM module');
      navigate('/dashboard');
      return;
    }
    
    fetchLeads();
    fetchStatistics();
    fetchNotifications();
  }, [navigate]);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crm/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
      setFilteredLeads(response.data);
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadOwnerOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crm/lead-owners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeadOwnerOptions(response.data?.owners || []);
    } catch (error) {
      console.error('Failed to fetch lead owners:', error);
      setLeadOwnerOptions([]);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crm/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to fetch statistics');
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crm/notifications?unread_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  useEffect(() => {
    applyFilters();
  }, [filters, leads]);

  useEffect(() => {
    fetchLeadOwnerOptions();
  }, [leads]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, leads]);

  // Reset bulk selection when filters change (avoid selecting filtered-out leads)
  useEffect(() => {
    setSelectedLeadIds(new Set());
  }, [filters]);

  const applyFilters = () => {
    let filtered = [...leads];
    
    if (filters.search) {
      filtered = filtered.filter(lead => 
        lead.hospital_name.toLowerCase().includes(filters.search.toLowerCase()) ||
        lead.id.toLowerCase().includes(filters.search.toLowerCase())
      );
    }
    
    if (filters.city) {
      const target = filters.city.trim().toLowerCase();
      filtered = filtered.filter(lead => (lead.city || '').trim().toLowerCase() === target);
    }
    
    if (filters.dispositionStatuses.length > 0) {
      filtered = filtered.filter(lead => filters.dispositionStatuses.includes(lead.disposition_status));
    }
    
    if (filters.leadOwner) {
      filtered = filtered.filter(lead => lead.lead_owner_name === filters.leadOwner);
    }
    
    // Sort by created_at in descending order (latest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
    
    setFilteredLeads(filtered);
  };

  const toggleDispositionStatus = (status) => {
    setFilters((prev) => {
      const isSelected = prev.dispositionStatuses.includes(status);
      return {
        ...prev,
        dispositionStatuses: isSelected
          ? prev.dispositionStatuses.filter((item) => item !== status)
          : [...prev.dispositionStatuses, status]
      };
    });
  };

  const clearDispositionStatusFilter = () => {
    setFilters((prev) => ({ ...prev, dispositionStatuses: [] }));
  };

  // Distinct, sorted, non-empty locations (city) present in the leads — powers the Location filter
  const locationOptions = Array.from(
    new Set((leads || []).map((lead) => (lead.city || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / PAGE_SIZE));
  const pageStartIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedLeads = filteredLeads.slice(pageStartIndex, pageStartIndex + PAGE_SIZE);

  const goToPage = (page) => {
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    setCurrentPage(nextPage);
  };

  const showingFrom = filteredLeads.length === 0 ? 0 : pageStartIndex + 1;
  const showingTo = Math.min(pageStartIndex + PAGE_SIZE, filteredLeads.length);

  // ── Bulk reassign (Super Admin only) ──────────────────────────────
  const isSuperAdmin = user?.role === 'Super Admin';
  // Real, active lead-owning users for the target picker (exclude synthetic "Assigned" entries)
  const assignableOwners = (leadOwnerOptions || []).filter((o) => o.id && o.role && o.role !== 'Assigned');
  const selectedCount = selectedLeadIds.size;
  const allPageSelected = paginatedLeads.length > 0 && paginatedLeads.every((l) => selectedLeadIds.has(l.id));
  const reassignTargetName = assignableOwners.find((o) => o.id === reassignTargetId)?.name || '';

  const toggleLeadSelection = (leadId) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (paginatedLeads.every((l) => next.has(l.id))) {
        paginatedLeads.forEach((l) => next.delete(l.id));
      } else {
        paginatedLeads.forEach((l) => next.add(l.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedLeadIds(new Set());

  const openReassign = () => {
    setReassignTargetId('');
    setReassignOpen(true);
  };

  const handleConfirmReassign = async () => {
    if (!reassignTargetId || selectedCount === 0) return;
    setReassigning(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/crm/leads/reassign`,
        { lead_ids: Array.from(selectedLeadIds), target_user_id: reassignTargetId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const count = response.data?.count ?? selectedCount;
      toast.success(`${count} lead(s) have been successfully reassigned to ${reassignTargetName}.`);
      setConfirmReassignOpen(false);
      setReassignOpen(false);
      clearSelection();
      await fetchLeads();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to reassign leads'));
    } finally {
      setReassigning(false);
    }
  };

  const handleDeactivateLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to deactivate this lead?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/crm/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lead deactivated successfully');
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate lead');
    }
  };

  const getDispositionColor = (status) => {
    const colors = {
      'Customer Reached Out': 'bg-blue-100 text-blue-800',
      'Demo Completed': 'bg-purple-100 text-purple-800',
      'Quotation Sent': 'bg-yellow-100 text-yellow-800',
      'Converted': 'bg-green-100 text-green-800',
      'Self-Registered': 'bg-teal-100 text-teal-800',
      'Implemented': 'bg-green-500 text-white',
      'Cold': 'bg-gray-100 text-gray-800',
      'Dropped': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatAddedOn = (value) => {
    if (!value) return '-';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '-';
    return dt.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading || !user) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading CRM...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Page Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-6">LEAD MANAGEMENT</h1>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-gray-200 p-1">
            <TabsTrigger value="leads" className="flex items-center space-x-2">
              <ClipboardList className="w-4 h-4" />
              <span>Leads</span>
            </TabsTrigger>
            {user?.role === 'Super Admin' && (
              <TabsTrigger value="users" className="flex items-center space-x-2">
                <UserCog className="w-4 h-4" />
                <span>User Management</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads" className="space-y-6">
            {/* Statistics Cards - Matching Dashboard Style */}
            {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium text-blue-700 mb-2">Total Leads</p>
                  <h3 className="text-4xl font-bold text-blue-900">{statistics.total_leads}</h3>
                </div>
                <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-700" />
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium text-green-700 mb-2">Converted</p>
                  <h3 className="text-4xl font-bold text-green-900">{statistics.disposition_breakdown?.Converted || 0}</h3>
                </div>
                <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-700" />
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Cold Leads</p>
                  <h3 className="text-4xl font-bold text-gray-900">{statistics.disposition_breakdown?.Cold || 0}</h3>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-gray-700" />
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium text-red-700 mb-2">Overdue Follow-ups</p>
                  <h3 className="text-4xl font-bold text-red-900">{statistics.overdue_follow_ups}</h3>
                </div>
                <div className="w-12 h-12 bg-red-200 rounded-lg flex items-center justify-center">
                  <Bell className="w-6 h-6 text-red-700" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-md p-6">
          {/* Header with Add Lead Button */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Leads</h2>
            <div className="flex space-x-2">
              {['CRM Associate', 'CRM Officer', 'Super Admin', 'Exlva Admin', 'ExlvaAdmin', 'Admin'].includes(user.role) && (
                <Button
                  data-testid="add-lead-btn"
                  onClick={() => {
                    setSelectedLeadIdForEdit(null);
                    setIsViewMode(false);
                    setIsModalOpen(true);
                  }}
                  className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Lead
                </Button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                data-testid="search-input"
                placeholder="Search leads..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
              />
            </div>
            <Select
              value={filters.city || 'all'}
              onValueChange={(value) => setFilters({ ...filters, city: value === 'all' ? '' : value })}
            >
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Filter by Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locationOptions.map((cityName) => (
                  <SelectItem key={cityName} value={cityName}>{cityName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.leadOwner || 'all'}
              onValueChange={(value) => setFilters({ ...filters, leadOwner: value === 'all' ? '' : value })}
            >
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Filter by Owner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Owners</SelectItem>
                {leadOwnerOptions.map((owner) => (
                  <SelectItem key={owner.id} value={owner.name}>{owner.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full h-10 justify-between font-normal text-gray-700">
                  <span>
                    {filters.dispositionStatuses.length > 0
                      ? `${filters.dispositionStatuses.length} status selected`
                      : 'Filter by Status'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[260px]" align="start">
                {DISPOSITION_STATUS_OPTIONS.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={filters.dispositionStatuses.includes(status)}
                    onCheckedChange={() => toggleDispositionStatus(status)}
                    onSelect={(event) => event.preventDefault()}
                  >
                    {status}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuItem onClick={clearDispositionStatusFilter}>Clear</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Contextual bulk-action bar (Super Admin only, shown when leads are selected) */}
          {isSuperAdmin && selectedCount > 0 && (
            <div className="flex items-center justify-between gap-3 mb-4 px-4 py-3 rounded-lg border border-blue-200 bg-blue-50">
              <span className="text-sm font-medium text-blue-900">
                {selectedCount} Lead{selectedCount === 1 ? '' : 's'} Selected
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
                  onClick={openReassign}
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Change Lead Owner
                </Button>
                <Button size="sm" variant="outline" onClick={clearSelection}>
                  Clear
                </Button>
              </div>
            </div>
          )}

          {/* Leads Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isSuperAdmin && (
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={allPageSelected}
                        onCheckedChange={toggleSelectAllOnPage}
                        aria-label="Select all leads on this page"
                      />
                    </TableHead>
                  )}
                  <TableHead>Hospital Name</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-center">Staff</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Quoted</TableHead>
                  <TableHead>Lead Owner</TableHead>
                  <TableHead className="w-[110px] text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 8 : 7} className="text-center py-8 text-gray-500">
                      No leads found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedLeads.map((lead) => (
                    <TableRow
                      key={lead.id}
                      data-testid={`lead-row-${lead.id}`}
                      className={lead.can_manage ? 'cursor-pointer' : ''}
                      onClick={lead.can_manage ? () => {
                        setSelectedLeadIdForEdit(lead.id);
                        setIsViewMode(true);
                        setIsModalOpen(true);
                      } : undefined}
                    >
                      {isSuperAdmin && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedLeadIds.has(lead.id)}
                            onCheckedChange={() => toggleLeadSelection(lead.id)}
                            aria-label={`Select lead ${lead.hospital_name}`}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="leading-tight">
                          <div className="font-medium text-gray-900 truncate">{lead.hospital_name}</div>
                          <div className="text-[11px] italic text-gray-500 truncate">
                            Added by: {lead.created_by_name || lead.lead_owner_name || 'System'}
                          </div>
                          <div className="text-[11px] italic text-gray-500 truncate">
                            Added on: {formatAddedOn(lead.created_at)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{lead.city}</TableCell>
                      <TableCell className="text-center">{lead.number_of_staff}</TableCell>
                      <TableCell>
                        <Badge className={getDispositionColor(lead.disposition_status)}>
                          {lead.disposition_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {lead.quoted_amount ? `₹${lead.quoted_amount.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell>{lead.lead_owner_name}</TableCell>
                      <TableCell>
                        {lead.can_manage ? (
                          <div className="flex items-center justify-end space-x-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              data-testid={`edit-lead-${lead.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setIsViewMode(false);
                                setSelectedLeadIdForEdit(lead.id);
                                setIsModalOpen(true);
                              }}
                              data-tooltip="Edit Lead"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-tooltip="More Actions">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => handleDeactivateLead(lead.id)}
                                  className="text-red-600"
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Deactivate
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                            <span className="text-[11px] italic text-gray-400">List only</span>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-4">
            <p className="text-sm text-gray-600">
              Showing {showingFrom}-{showingTo} of {filteredLeads.length} leads
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
          </TabsContent>

          {/* User Management Tab - Super Admin Only */}
          {user?.role === 'Super Admin' && (
            <TabsContent value="users">
              <UserManagementTab />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Lead Form Modal */}
      <LeadFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedLeadIdForEdit(null);
          setIsViewMode(false);
        }}
        leadId={selectedLeadIdForEdit}
        viewOnly={isViewMode}
        onSuccess={() => fetchLeads()}
        onEditExisting={(existingLeadId) => {
          // Switch the open modal to edit the existing (duplicate) lead
          setIsViewMode(false);
          setSelectedLeadIdForEdit(existingLeadId);
        }}
      />

      {/* Change Lead Owner — target picker */}
      <Dialog open={reassignOpen} onOpenChange={(open) => { if (!open) setReassignOpen(false); }}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Change Lead Owner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Current Selection: <span className="font-semibold">{selectedCount} Lead{selectedCount === 1 ? '' : 's'} Selected</span>
            </p>
            <div className="space-y-1.5">
              <Label>New Lead Owner</Label>
              <Select value={reassignTargetId} onValueChange={setReassignTargetId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a lead owner" />
                </SelectTrigger>
                <SelectContent>
                  {assignableOwners.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">No eligible users found</div>
                  ) : (
                    assignableOwners.map((owner) => (
                      <SelectItem key={owner.id} value={owner.id}>
                        {owner.name}{owner.role ? ` (${owner.role})` : ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setReassignOpen(false)}>Cancel</Button>
              <Button
                className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
                disabled={!reassignTargetId}
                onClick={() => setConfirmReassignOpen(true)}
              >
                Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reassign confirmation */}
      <Dialog open={confirmReassignOpen} onOpenChange={(open) => { if (!open) setConfirmReassignOpen(false); }}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Confirm Reassignment</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700">
            Are you sure you want to reassign the selected lead(s) to <span className="font-semibold">{reassignTargetName}</span>?
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setConfirmReassignOpen(false)} disabled={reassigning}>
              Cancel
            </Button>
            <Button
              className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
              onClick={handleConfirmReassign}
              disabled={reassigning}
            >
              {reassigning ? 'Reassigning...' : 'Yes, Reassign'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default CRMDashboard;